import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useClient } from '../../context/ClientContext';
import api from '../../services/api';
import { Client } from '../../types';
import DashboardHeader from '../dashboard/DashboardHeader';
import Footer from '../dashboard/Footer';
import '../opted/OptedListPage.css';

export interface ReviewInviteEmailRow {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  sent_at: string | null;
  completed: boolean;
  review_completed: string;
  completed_at: string | null;
  sent_by: string | null;
}

const SentReviewEmailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { activeClients, selectedClientId } = useClient();
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState<number | null>(null);
  const [rows, setRows] = useState<ReviewInviteEmailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'yes' | 'no'>('all');

  const isAdminUser = isAdmin();
  const clientList = isAdminUser ? clients : activeClients;

  useEffect(() => {
    if (isAdminUser) {
      api.get<Client[]>('/clients').then((res) => {
        setClients(res.data);
        if (res.data.length > 0 && clientId === null) {
          setClientId(res.data[0].id);
        }
      }).catch(() => setClients([]));
    }
  }, [isAdminUser]);

  useEffect(() => {
    if (!isAdminUser) {
      if (selectedClientId != null) {
        setClientId(selectedClientId);
      } else if (activeClients.length > 0 && clientId === null) {
        setClientId(activeClients[0].id);
      }
    }
  }, [isAdminUser, activeClients, selectedClientId, clientId]);

  useEffect(() => {
    if (!clientId) {
      setRows([]);
      setListLoading(false);
      setLoading(false);
      return;
    }
    setListLoading(true);
    const params: Record<string, string | number> = { client_id: clientId };
    if (statusFilter === 'yes') params.completed = 1;
    if (statusFilter === 'no') params.completed = 0;

    api.get<ReviewInviteEmailRow[]>('/review-invite-emails', { params })
      .then((res) => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => { setListLoading(false); setLoading(false); });
  }, [clientId, statusFilter]);

  return (
    <div className="dashboard">
      <DashboardHeader title="Sent Review Emails" showNav={false} showEmailButton={false} />
      <div className="dashboard-content">
        <div className="opted-list-section">
          <div className="detail-header">
            <button
              type="button"
              onClick={() => navigate(isAdminUser ? '/dashboard?tab=reviews' : '/dashboard')}
              className="back-button"
            >
              ← Back
            </button>
            <h1>Sent Review Emails</h1>
          </div>

          <div className="opted-list-card">
            <div className="opted-client-select">
              <label htmlFor="sent-email-client">Company</label>
              <select
                id="sent-email-client"
                value={clientId ?? ''}
                onChange={(e) => {
                  setClientId(e.target.value === '' ? null : Number(e.target.value));
                }}
              >
                <option value="">-- Select company --</option>
                {clientList.map((c) => (
                  <option key={c.id} value={c.id}>{c.client_name}</option>
                ))}
              </select>
            </div>

            <div className="opted-client-select">
              <label htmlFor="sent-email-status">Review Completed</label>
              <select
                id="sent-email-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'yes' | 'no')}
              >
                <option value="all">All</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {listLoading || loading ? (
              <div className="loading">Loading...</div>
            ) : (
              <div className="opted-table-wrap">
                <table className="opted-table">
                  <thead>
                    <tr>
                      <th>Date / Time Sent</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Review Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="no-rows">
                          No review emails have been sent for this company.
                        </td>
                      </tr>
                    ) : (
                      rows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.sent_at || '—'}</td>
                          <td>{row.name}</td>
                          <td>{row.email}</td>
                          <td>{row.review_completed}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SentReviewEmailsPage;
